/**
 * ============================================================
 * STANDARDIZED LOADING & STATE COMPONENTS
 * ============================================================
 * 
 * Unified loading, empty, error, and retry states for
 * consistent UX across all portals.
 */

import React, { memo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { 
  RefreshCw, 
  AlertCircle, 
  WifiOff, 
  FileX, 
  Inbox,
  Loader2
} from 'lucide-react';

// ============================================================
// SKELETON VARIANTS
// ============================================================

/**
 * Card skeleton for job cards, panels, etc.
 */
export const CardSkeleton = memo(function CardSkeleton({ className = '' }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-8 w-20 rounded-lg" />
        <Skeleton className="h-8 w-20 rounded-lg" />
      </div>
    </div>
  );
});

/**
 * Table row skeleton
 */
export const TableRowSkeleton = memo(function TableRowSkeleton({ columns = 5 }) {
  return (
    <tr className="border-b border-slate-100">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-4 w-full" />
        </td>
      ))}
    </tr>
  );
});

/**
 * List item skeleton
 */
export const ListItemSkeleton = memo(function ListItemSkeleton({ hasAvatar = false }) {
  return (
    <div className="flex items-center gap-3 p-3 border-b border-slate-100">
      {hasAvatar && <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />}
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
});

/**
 * Communication/message skeleton
 */
export const MessageSkeleton = memo(function MessageSkeleton({ isReply = false }) {
  return (
    <div className={`p-3 ${isReply ? 'ml-8' : ''}`}>
      <div className="flex items-start gap-3">
        <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </div>
  );
});

/**
 * Grid of card skeletons
 */
export const CardGridSkeleton = memo(function CardGridSkeleton({ count = 6, columns = 3 }) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };
  
  return (
    <div className={`grid gap-4 ${gridCols[columns] || gridCols[3]}`}>
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
});


// ============================================================
// EMPTY STATES
// ============================================================

/**
 * Generic empty state component
 */
export const EmptyState = memo(function EmptyState({
  icon: Icon = Inbox,
  title = 'No items found',
  description = 'There are no items to display.',
  action,
  actionLabel = 'Add Item',
  className = ''
}) {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`} data-testid="empty-state">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <Icon className="h-8 w-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700 mb-1">{title}</h3>
      <p className="text-sm text-slate-500 max-w-sm mb-4">{description}</p>
      {action && (
        <Button onClick={action} size="sm">
          {actionLabel}
        </Button>
      )}
    </div>
  );
});

/**
 * No results empty state (for search/filter)
 */
export const NoResultsState = memo(function NoResultsState({
  searchTerm = '',
  onClear,
  className = ''
}) {
  return (
    <EmptyState
      icon={FileX}
      title="No results found"
      description={searchTerm 
        ? `No items match "${searchTerm}". Try adjusting your search or filters.`
        : 'No items match your current filters.'
      }
      action={onClear}
      actionLabel="Clear filters"
      className={className}
    />
  );
});


// ============================================================
// ERROR STATES
// ============================================================

/**
 * Error state with retry option
 */
export const ErrorState = memo(function ErrorState({
  title = 'Something went wrong',
  message = 'Failed to load data. Please try again.',
  onRetry,
  className = ''
}) {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`} data-testid="error-state">
      <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
        <AlertCircle className="h-8 w-8 text-red-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700 mb-1">{title}</h3>
      <p className="text-sm text-slate-500 max-w-sm mb-4">{message}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
      )}
    </div>
  );
});

/**
 * Offline state
 */
export const OfflineState = memo(function OfflineState({
  onRetry,
  className = ''
}) {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`} data-testid="offline-state">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <WifiOff className="h-8 w-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700 mb-1">You're offline</h3>
      <p className="text-sm text-slate-500 max-w-sm mb-4">
        Please check your internet connection and try again.
      </p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
      )}
    </div>
  );
});


// ============================================================
// LOADING INDICATORS
// ============================================================

/**
 * Inline loading spinner
 */
export const LoadingSpinner = memo(function LoadingSpinner({
  size = 'md',
  className = ''
}) {
  const sizes = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12'
  };
  
  return (
    <Loader2 className={`animate-spin text-emerald-600 ${sizes[size] || sizes.md} ${className}`} />
  );
});

/**
 * Full page loading state
 */
export const PageLoading = memo(function PageLoading({ message = 'Loading...' }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center py-20" data-testid="page-loading">
      <LoadingSpinner size="lg" className="mb-4" />
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  );
});

/**
 * Inline loading indicator for buttons/actions
 */
export const InlineLoading = memo(function InlineLoading({ className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <LoadingSpinner size="xs" />
      <span className="text-xs text-slate-500">Loading...</span>
    </span>
  );
});

/**
 * Load more button/indicator
 */
export const LoadMoreIndicator = memo(function LoadMoreIndicator({
  loading = false,
  hasMore = true,
  onLoadMore,
  className = ''
}) {
  if (!hasMore) {
    return (
      <div className={`text-center py-4 text-xs text-slate-400 ${className}`}>
        No more items to load
      </div>
    );
  }
  
  return (
    <div className={`flex justify-center py-4 ${className}`}>
      {loading ? (
        <InlineLoading />
      ) : (
        <Button variant="ghost" size="sm" onClick={onLoadMore} className="text-emerald-600">
          Load more
        </Button>
      )}
    </div>
  );
});


// ============================================================
// RETRY WRAPPER
// ============================================================

/**
 * Wrapper component with automatic retry capability
 */
export const RetryWrapper = memo(function RetryWrapper({
  loading,
  error,
  empty,
  onRetry,
  emptyTitle,
  emptyDescription,
  children,
  loadingComponent,
  className = ''
}) {
  if (loading) {
    return loadingComponent || <PageLoading />;
  }
  
  if (error) {
    return (
      <ErrorState
        message={error}
        onRetry={onRetry}
        className={className}
      />
    );
  }
  
  if (empty) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        className={className}
      />
    );
  }
  
  return children;
});


// ============================================================
// EXPORTS
// ============================================================

export default {
  CardSkeleton,
  TableRowSkeleton,
  ListItemSkeleton,
  MessageSkeleton,
  CardGridSkeleton,
  EmptyState,
  NoResultsState,
  ErrorState,
  OfflineState,
  LoadingSpinner,
  PageLoading,
  InlineLoading,
  LoadMoreIndicator,
  RetryWrapper
};
